import clsx from 'clsx'
// eslint-disable-next-line no-restricted-imports
import NextImage, {
  ImageProps as NextImageProps,
  StaticImageData,
} from 'next/image'
import Button, { ButtonProps } from '../Button/Button'

interface ImageButton extends ButtonProps {
  text: string
}

type ImageDimension = number | `${number}` | 'auto' | string

export interface ImageStandAloneProps
  extends Omit<NextImageProps, 'src' | 'alt' | 'width' | 'height'> {
  className?: string
  width?: ImageDimension
  height?: ImageDimension
  alt?: string
  src: string | StaticImageData | { src: string }
  style?: React.CSSProperties
}

export interface ImageProps {
  /**
   * Image's ClassName (Optional)
   */
  className?: string
  /**
   * Header copy above the image (Optional)
   */
  header?: string
  /**
   * Caption copy for the image (Optional)
   */
  caption?: string
  /**
   * Body copy below the image and caption (Optional)
   */
  body?: string
  /**
   * Link Button (Optional)
   */
  button?: ImageButton
  /**
   * Image with its respective attributes
   */
  image: ImageStandAloneProps
}

const DEFAULT_IMG_DIMENSION = 1

const WIDTH_CLASS_REGEX = /(^|\s)(?:!*)?(?:[a-z-]+:)?(?:min-)?w-(?!\[?auto\b)/i
const HEIGHT_CLASS_REGEX = /(^|\s)(?:!*)?(?:[a-z-]+:)?(?:min-)?h-(?!\[?auto\b)/i

const hasDimensionUtility = (
  className: string | undefined,
  dimension: 'w' | 'h',
) => {
  if (!className) return false
  const matcher = dimension === 'w' ? WIDTH_CLASS_REGEX : HEIGHT_CLASS_REGEX
  return matcher.test(className)
}

const parseDimension = (value?: ImageDimension): number | undefined => {
  if (value === undefined || value === null || value === 'auto')
    return undefined
  if (typeof value === 'number') return value
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const isStaticImageData = (
  value: ImageStandAloneProps['src'],
): value is StaticImageData => {
  return (
    !!value &&
    typeof value === 'object' &&
    'src' in value &&
    'width' in value &&
    'height' in value &&
    typeof (value as StaticImageData).width === 'number' &&
    typeof (value as StaticImageData).height === 'number'
  )
}

const isObjectWithSrc = (
  value: ImageStandAloneProps['src'],
): value is { src: string } => {
  return (
    !!value &&
    typeof value === 'object' &&
    'src' in value &&
    typeof (value as { src: unknown }).src === 'string'
  )
}

export function ImageStandAlone(props: Readonly<ImageStandAloneProps>) {
  const { src, alt, width, height, className, style, unoptimized, ...rest } =
    props
  const staticImage = isStaticImageData(src) ? src : undefined
  const resolvedSrc: string | StaticImageData = staticImage
    ? staticImage
    : isObjectWithSrc(src)
      ? src.src
      : (src as string)
  const numericWidth = parseDimension(width) ?? staticImage?.width
  const numericHeight = parseDimension(height) ?? staticImage?.height

  const shouldForceAutoWidth =
    numericWidth === undefined &&
    !style?.width &&
    !hasDimensionUtility(className, 'w')
  const shouldForceAutoHeight =
    numericHeight === undefined &&
    !style?.height &&
    !hasDimensionUtility(className, 'h')

  const computedStyle =
    shouldForceAutoWidth || shouldForceAutoHeight
      ? {
          ...style,
          ...(shouldForceAutoWidth ? { width: 'auto' } : {}),
          ...(shouldForceAutoHeight ? { height: 'auto' } : {}),
        }
      : style

  const resolvedUnoptimized =
    unoptimized ?? (numericWidth === undefined || numericHeight === undefined)

  return (
    <NextImage
      {...rest}
      className={className}
      alt={alt ?? ''}
      src={resolvedSrc}
      width={numericWidth ?? DEFAULT_IMG_DIMENSION}
      height={numericHeight ?? DEFAULT_IMG_DIMENSION}
      style={computedStyle}
      unoptimized={resolvedUnoptimized}
    />
  )
}

// Usage in your component
export default function Image({
  className,
  header,
  caption,
  body,
  button,
  image,
}: Readonly<ImageProps>) {
  return (
    <figure className={clsx('flex flex-col gap-y-4', className)}>
      {header ? (
        <h4 className={clsx('type-headline-1 truncate')}>{header}</h4>
      ) : null}
      <ImageStandAlone
        {...image}
        className={clsx(
          'rounded-8 w-full h-auto object-cover object-center aspect-square md:aspect-auto',
          image.className,
        )}
      />
      {caption ? (
        <figcaption className="type-body-7-normal text-neutral-600">
          {caption}
        </figcaption>
      ) : null}
      {body ? (
        <p className={clsx('max-w-[864px] type-body-6 line-clamp-4')}>{body}</p>
      ) : null}
      {button ? (
        <Button className="self-start type-body-8 md:type-body-5" {...button}>
          {button.text}
        </Button>
      ) : null}
    </figure>
  )
}
